import React, { useRef, useEffect, useState, useMemo } from "react";
import { Box, Typography, Button, Stack, IconButton } from "@mui/material";
import { Ion, Viewer, createWorldTerrainAsync, createOsmBuildingsAsync, Cartesian3, Cartesian2, Math as CesiumMath, Entity, EllipseGraphics, LabelGraphics, Color, HeightReference, Cesium3DTileset, Cesium3DTileFeature, SceneMode, Transforms, Matrix4 } from "cesium";
import "cesium/Build/Cesium/Widgets/widgets.css";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import CompareIcon from "@mui/icons-material/Compare";
import TimelineIcon from "@mui/icons-material/Timeline";
import FilterIcon from "@mui/icons-material/FilterList";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import MapIcon from "@mui/icons-material/Map";
import { useNavigate } from "react-router-dom";
import "./ThreeDMap.css";
import { CESIUM_ION_ACCESS_TOKEN } from "./CesiumConfig";
import CareNetworkTray from "./CareNetworkTray";
import ScenarioBuilderTray from './ScenarioBuilderTray';
import MarketIntelligenceTray from "./MarketIntelligenceTray";
import PopulationCohortsTray from "./PopulationCohortsTray";
import { MapToolbar, ToolbarMode } from "./components/MapToolbar";

// Set Cesium Ion access token
Ion.defaultAccessToken = CESIUM_ION_ACCESS_TOKEN;

// Suppress Cesium console logs for cleaner debugging
if (typeof window !== 'undefined') {
  // Store original console methods
  const originalConsoleError = console.error;
  const originalConsoleWarn = console.warn;
  const originalConsoleLog = console.log;
  
  // Override console methods to filter out Cesium noise
  console.error = (...args: any[]) => {
    const message = String(args[0] || '');
    // Only show non-Cesium errors
    if (!message.includes('Cesium') && !message.includes('cesium') && !message.includes('b3dm') && !message.includes('Layout was forced')) {
      originalConsoleError.apply(console, args);
    }
  };
  
  console.warn = (...args: any[]) => {
    const message = String(args[0] || '');
    // Only show non-Cesium warnings
    if (!message.includes('Cesium') && !message.includes('cesium') && !message.includes('b3dm')) {
      originalConsoleWarn.apply(console, args);
    }
  };
  
  // Filter console.log to only show our debug messages
  console.log = (...args: any[]) => {
    const message = String(args[0] || '');
    // Show only our debug logs (they contain emoji or specific keywords)
    if (message.includes('🔍') || 
        message.includes('📍') || 
        message.includes('✅') || 
        message.includes('❌') || 
        message.includes('⚠️') || 
        message.includes('ℹ️') ||
        message.includes('🌐') ||
        message.includes('Reading search params') ||
        message.includes('Fetching geocode') ||
        message.includes('CONDITIONS MET') ||
        message.includes('addPatientLeakageEntities') ||
        message.includes('Entities added')) {
      originalConsoleLog.apply(console, args);
    }
    // Suppress everything else (React DevTools, Cesium, etc.)
  };
}

const ThreeDMap: React.FC = () => {
  const cesiumContainer = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<Viewer | null>(null);
  const buildingsTilesetRef = useRef<Cesium3DTileset | null>(null);
  // Note: We read search params directly from window.location.href to avoid re-render issues
  // with useSearchParams hook which subscribes to URL changes
  const navigate = useNavigate();
  const processedSearchRef = useRef<string | null>(null);
  const entitiesAddedRef = useRef<boolean>(false);
  const searchProcessedRef = useRef<boolean>(false);
  const cameraFlyingRef = useRef<boolean>(false); // Track if camera is currently flying to a search location

  // Add state for the Scenario Builder tray
  const [showScenarioTray, setShowScenarioTray] = useState(false);
  // Add state for the Care Network tray
  const [showCareNetworkTray, setShowCareNetworkTray] = useState(false);
  // Add state for the Market Intelligence tray
  const [showMarketIntelligenceTray, setShowMarketIntelligenceTray] = useState(false);
  // Add state for the Population Cohorts tray
  const [showPopulationCohortsTray, setShowPopulationCohortsTray] = useState(false);

  // Map toolbar state
  const [toolbarMode, setToolbarMode] = useState<ToolbarMode>('selection');
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Scenario builder state and config (copied from ScenarioBuilder.tsx)
  const SCENARIO_TYPES = {
    'Market Expansion': {
      metrics: {
        netPatientRevenue: { label: 'Target Revenue Growth (%)', min: -50, max: 100 },
        marketShare: { label: 'Target Market Share (%)', min: 0, max: 100 },
        patientLeakage: { label: 'Expected Leakage Reduction (%)', min: -50, max: 0 },
        waitTimes: { label: 'Target Wait Time Reduction (%)', min: -50, max: 0 },
        careScores: { label: 'Target Care Score Improvement (%)', min: 0, max: 50 },
        expenses: { label: 'Expected Cost Increase (%)', min: 0, max: 100 },
        irr: { label: 'Target IRR (%)', min: 0, max: 50 },
        npv: { label: 'Target NPV ($)', min: 0, max: 10000000 },
        paybackPeriod: { label: 'Target Payback Period (months)', min: 1, max: 60 },
        coveredLives: { label: 'Target Covered Lives Increase (%)', min: 0, max: 100 }
      }
    },
    // ...other scenario types (copy as needed)...
  };

  // Use useMemo to prevent recreating defaultScenario on every render
  // This prevents unnecessary re-renders and state updates
  const defaultScenario = useMemo(() => ({
    id: '1',
    name: 'Market Expansion - Cardiac Care',
    type: 'Market Expansion',
    status: 'draft',
    parameters: {
      timeframe: {
        lookbackPeriod: 12,
        startDate: new Date(),
        completionDate: new Date(Date.now() + 180 * 24 * 60 * 60 * 1000),
        projectLife: 60
      },
      budget: {
        min: 1000000,
        max: 5000000
      },
      metrics: {
        netPatientRevenue: 15,
        marketShare: 25,
        patientLeakage: -10,
        waitTimes: -20,
        readmissionRates: -5,
        careScores: 10,
        expenses: -15,
        irr: 20,
        npv: 3000000,
        paybackPeriod: 36,
        baselineCosts: -10,
        coveredLives: 15
      },
      filters: {
        facilities: [],
        providers: [],
        geography: {
          type: 'zip',
          value: ''
        },
        specialties: ['Cardiology'],
        patientCohorts: [],
        organizations: []
      }
    }
  }), []); // Empty dependency array - only create once

  const [selectedScenario, setSelectedScenario] = useState(defaultScenario);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isViewerReady, setIsViewerReady] = useState(false);

  /**
   * Helper function to convert local east-north-up offset to global Cartesian coordinates
   * This ensures proper coordinate system transformation for camera positioning
   * @param targetPosition - The target position in Cartesian coordinates
   * @param eastOffset - Offset in meters east (positive) or west (negative)
   * @param northOffset - Offset in meters north (positive) or south (negative)
   * @param upOffset - Offset in meters up (positive) or down (negative)
   * @returns The camera position in global Cartesian coordinates
   */
  const calculateCameraPositionFromOffset = (
    targetPosition: Cartesian3,
    eastOffset: number,
    northOffset: number,
    upOffset: number
  ): Cartesian3 => {
    // Get the transformation matrix from local east-north-up frame to global Cartesian
    const transform = Transforms.eastNorthUpToFixedFrame(targetPosition);
    
    // Create local offset vector (east, north, up)
    const localOffset = new Cartesian3(eastOffset, northOffset, upOffset);
    
    // Transform local offset to global Cartesian coordinates
    const globalOffset = Matrix4.multiplyByPointAsVector(transform, localOffset, new Cartesian3());
    
    // Add the transformed offset to the target position
    return Cartesian3.add(targetPosition, globalOffset, new Cartesian3());
  };

  useEffect(() => {
    if (cesiumContainer.current && !viewerRef.current) {
      // Create the Cesium Viewer
      viewerRef.current = new Viewer(cesiumContainer.current, {
        geocoder: true, // Enable Cesium's built-in geocoder widget
        timeline: false,
        animation: false,
        baseLayerPicker: true,
        homeButton: false, // Disable home button (we have custom camera controls)
        sceneModePicker: false,
        navigationHelpButton: false,
        fullscreenButton: false, // Disable default fullscreen button (we have our own in toolbar)
        infoBox: true,
        selectionIndicator: true,
        shouldAnimate: true,
      });

      // Override geocoder's default camera behavior to use our 3D view settings
      // The geocoder widget defaults to looking straight down (pitch = -90 degrees)
      // We listen for camera movement completion and adjust pitch if it's too steep
      let lastCameraPitch = viewerRef.current.camera.pitch;
      let lastCameraPosition: Cartesian3 | null = null;
      let isFixingCamera = false; // Flag to prevent infinite loop
      
      // Listen for camera movement completion and fix pitch if needed
      const cameraMoveEndHandler = () => {
        if (!viewerRef.current || isFixingCamera) return; // Prevent re-entry
        
        const currentPitch = viewerRef.current.camera.pitch;
        const pitchDegrees = CesiumMath.toDegrees(currentPitch);
        const currentPosition = viewerRef.current.camera.position.clone();
        
        // Detect if camera just moved significantly (likely from geocoder search)
        const positionChanged = !lastCameraPosition || 
          Cartesian3.distance(currentPosition, lastCameraPosition) > 1000;
        
        // If pitch is too steep (looking almost straight down) and position changed significantly, fix it
        // This catches geocoder searches which typically result in very steep pitch
        if (pitchDegrees < -75 && positionChanged) {
          isFixingCamera = true; // Set flag to prevent loop
          
          const pos = viewerRef.current.camera.positionCartographic;
          const lon = CesiumMath.toDegrees(pos.longitude);
          const lat = CesiumMath.toDegrees(pos.latitude);
          
          console.log('🔧 Fixing camera to street view at', lon, lat);
          
          // Ensure 3D mode
          viewerRef.current.scene.mode = SceneMode.SCENE3D;
          
          // Position camera at elevated height to see over foreground buildings and view target building
          const targetPosition = Cartesian3.fromDegrees(lon, lat, 0);
          // Use helper function to properly convert local east-north-up offset to global Cartesian coordinates
          // Parameters: 0m east, 150m north (closer to target), 400m up (elevated view)
          const cameraPosition = calculateCameraPositionFromOffset(targetPosition, 0, 150, 400);
          
          // Calculate correct orientation using lookAt (offset is in local frame for lookAt)
          const currentPos = viewerRef.current.camera.position.clone();
          const lookAtOffset = new Cartesian3(0, 150, 400); // Local frame offset for lookAt
          viewerRef.current.camera.lookAt(targetPosition, lookAtOffset);
          const finalHeading = viewerRef.current.camera.heading;
          const finalPitch = viewerRef.current.camera.pitch;
          
          // Reset to current position, then fly to street view
          viewerRef.current.camera.position = currentPos;
          
          // Update tracking BEFORE calling flyTo to prevent triggering handler again
          lastCameraPosition = cameraPosition;
          lastCameraPitch = finalPitch;
          
          // Fly to street view
          viewerRef.current.camera.flyTo({
            destination: cameraPosition,
            orientation: {
              heading: finalHeading,
              pitch: finalPitch,
              roll: 0.0
            },
            duration: 0.8,
            complete: () => {
              // Reset flag after camera adjustment completes
              setTimeout(() => {
                isFixingCamera = false;
              }, 100);
            }
          });
          
          // Highlight buildings after camera adjustment
          setTimeout(() => {
            if (viewerRef.current && buildingsTilesetRef.current) {
              highlightBuildingsNearLocation(viewerRef.current, buildingsTilesetRef.current, lon, lat, 50);
            }
          }, 1000);
        } else {
          // Update tracking variables for normal camera movements
          lastCameraPitch = currentPitch;
          lastCameraPosition = currentPosition;
        }
      };
      
      viewerRef.current.camera.moveEnd.addEventListener(cameraMoveEndHandler);
      // Store handler reference for cleanup
      (viewerRef.current.camera as any)._cameraMoveEndHandler = cameraMoveEndHandler;

      // Check if there's a search query in URL before setting initial camera
      // If there's a search, the search handler will set the camera position
      const currentUrl = new URL(window.location.href);
      const hasSearchQuery = currentUrl.searchParams.get('search');
      
      // Only set initial camera view if there's no search query
      // Otherwise, let the search handler set the camera position
      if (!hasSearchQuery && !cameraFlyingRef.current) {
        viewerRef.current.camera.flyTo({
          destination: Cartesian3.fromDegrees(-80.1918, 25.7617, 400),
          orientation: {
            heading: CesiumMath.toRadians(0.0),
            pitch: CesiumMath.toRadians(-15.0),
          },
          duration: 2,
        });
      }

      // Add terrain and OSM buildings
      (async () => {
        const terrain = await createWorldTerrainAsync();
        viewerRef.current!.terrainProvider = terrain;
        const buildings = await createOsmBuildingsAsync();
        buildingsTilesetRef.current = buildings; // Store reference for building highlighting
        viewerRef.current!.scene.primitives.add(buildings);
        
        // Mark viewer as ready after initialization is complete
        setIsViewerReady(true);
      })();
    }

    // Cleanup on unmount only
    // Note: Do not set isViewerReady to false here as it can cause re-render loops
    // The component is unmounting, so state updates are unnecessary
    return () => {
      if (viewerRef.current) {
        // Remove camera moveEnd event listener if it exists
        if (viewerRef.current.camera && (viewerRef.current.camera as any)._cameraMoveEndHandler) {
          viewerRef.current.camera.moveEnd.removeEventListener((viewerRef.current.camera as any)._cameraMoveEndHandler);
        }
        viewerRef.current.destroy();
        viewerRef.current = null;
        // Don't set isViewerReady(false) here - component is unmounting anyway
      }
    };
  }, []); // Empty dependency array ensures this only runs once on mount


  // Handle search query from URL parameters - read once when viewer is ready
  useEffect(() => {
    if (!isViewerReady || !viewerRef.current) {
      return;
    }

    // Read search params directly from current URL to avoid dependency issues
    const currentUrl = new URL(window.location.href);
    const searchQuery = currentUrl.searchParams.get('search');
    const alertType = currentUrl.searchParams.get('alert');
    
    // Don't process if there's no search query
    if (!searchQuery) {
      return;
    }
    
    // Check if we've already processed this exact search query
    const currentSearchKey = `${searchQuery}-${alertType || 'none'}`;
    if (processedSearchRef.current === currentSearchKey) {
      console.log('🔍 Search already processed, skipping:', currentSearchKey);
      return;
    }
    
    // Mark as processed immediately to prevent re-processing
    searchProcessedRef.current = true;
    processedSearchRef.current = currentSearchKey;
    
    console.log('🔍 Processing new search query:', searchQuery, 'alertType:', alertType);
    
    // Use Cesium Ion Geocoding Service directly for more control
    const geocodeUrl = `https://api.cesium.com/v1/geocode/search?text=${encodeURIComponent(searchQuery)}&access_token=${CESIUM_ION_ACCESS_TOKEN}`;
    
    console.log('🌐 Fetching geocode for:', searchQuery);
    
    fetch(geocodeUrl)
      .then(response => response.json())
      .then((data: any) => {
        if (!viewerRef.current) {
          console.log('❌ viewerRef.current is null after fetch');
          return;
        }
        
        console.log('📍 Geocoding response received:', data);
        
        if (data && data.results && data.results.length > 0) {
          const firstResult = data.results[0];
          const lon = firstResult.longitude;
          const lat = firstResult.latitude;
          
          console.log('📍 Geocoded coordinates:', lon, lat);
          console.log('🔍 Alert type:', alertType, '===', 'patient-leakage', '?', alertType === 'patient-leakage');
          console.log('🔍 Search query:', searchQuery, 'includes miami?', searchQuery.toLowerCase().includes('miami'));
          
          if (lon !== undefined && lat !== undefined) {
            // Add alert-specific entities if this is from the patient leakage alert
            const isPatientLeakage = alertType === 'patient-leakage';
            const isMiamiDade = searchQuery.toLowerCase().includes('miami');
            
            // Ensure we're in 3D mode (not 2D or Columbus View) to see buildings properly
            viewerRef.current.scene.mode = SceneMode.SCENE3D;
            
            if (isPatientLeakage && isMiamiDade) {
              console.log('✅✅✅ CONDITIONS MET: Adding patient leakage entities at:', lon, lat);
              if (!entitiesAddedRef.current) {
                addPatientLeakageEntities(viewerRef.current, lon, lat);
                entitiesAddedRef.current = true;
                
                // Highlight buildings near the searched location
                if (buildingsTilesetRef.current) {
                  highlightBuildingsNearLocation(viewerRef.current, buildingsTilesetRef.current, lon, lat, 50);
                }
                
                // Fly to optimal view of the target building
                cameraFlyingRef.current = true;
                const targetPosition = Cartesian3.fromDegrees(lon, lat, 0);
                // Use helper function to properly convert local east-north-up offset to global Cartesian coordinates
                // Parameters: 0m east, 150m north (closer to target), 400m up (elevated view)
                const cameraPosition = calculateCameraPositionFromOffset(targetPosition, 0, 150, 400);
                
                // Use lookAt to calculate correct orientation (offset is in local frame for lookAt)
                const lookAtOffset = new Cartesian3(0, 150, 400); // Local frame offset for lookAt
                viewerRef.current.camera.lookAt(targetPosition, lookAtOffset);
                const finalHeading = viewerRef.current.camera.heading;
                const finalPitch = viewerRef.current.camera.pitch;
                
                // Reset to current position, then fly to optimal view
                const currentPos = viewerRef.current.camera.position.clone();
                viewerRef.current.camera.position = currentPos;
                
                viewerRef.current.camera.flyTo({
                  destination: cameraPosition,
                  orientation: {
                    heading: finalHeading,
                    pitch: finalPitch,
                  },
                  duration: 2.0,
                  complete: () => {
                    setTimeout(() => {
                      cameraFlyingRef.current = false;
                    }, 100);
                  }
                });
              } else {
                console.log('⚠️ Entities already added, skipping');
              }
            } else {
              console.log('ℹ️ Not patient leakage alert - flying to location without entities');
              
              // Highlight buildings near the searched location
              if (buildingsTilesetRef.current) {
                highlightBuildingsNearLocation(viewerRef.current, buildingsTilesetRef.current, lon, lat, 50);
              }
              
              // Fly to optimal view of the target building
              cameraFlyingRef.current = true;
              const targetPosition = Cartesian3.fromDegrees(lon, lat, 0);
              // Use helper function to properly convert local east-north-up offset to global Cartesian coordinates
              // Parameters: 0m east, 150m north (closer to target), 400m up (elevated view)
              const cameraPosition = calculateCameraPositionFromOffset(targetPosition, 0, 150, 400);
              
              // Use lookAt to calculate correct orientation (offset is in local frame for lookAt)
              const lookAtOffset = new Cartesian3(0, 150, 400); // Local frame offset for lookAt
              viewerRef.current.camera.lookAt(targetPosition, lookAtOffset);
              const finalHeading = viewerRef.current.camera.heading;
              const finalPitch = viewerRef.current.camera.pitch;
              
              // Reset to current position, then fly to optimal view
              const currentPos = viewerRef.current.camera.position.clone();
              viewerRef.current.camera.position = currentPos;
              
              viewerRef.current.camera.flyTo({
                destination: cameraPosition,
                orientation: {
                  heading: finalHeading,
                  pitch: finalPitch,
                },
                duration: 2.0,
                complete: () => {
                  setTimeout(() => {
                    cameraFlyingRef.current = false;
                  }, 100);
                }
              });
            }
          } else {
            console.error('❌ Invalid coordinates from geocoding:', lon, lat);
          }
        } else {
          console.error('❌ No geocoding results found');
        }
      })
      .catch((error: any) => {
        console.error('❌ Geocoding failed:', error);
      });
  }, [isViewerReady]); // Only depend on isViewerReady - read URL params once

  // Function to highlight buildings near a searched address location
  // Uses industry-standard approach: traverse tiles, calculate distance, apply per-feature styling
  const highlightBuildingsNearLocation = (
    viewer: Viewer,
    tileset: Cesium3DTileset,
    targetLon: number,
    targetLat: number,
    radiusMeters: number = 50
  ) => {
    console.log('🏢 Highlighting buildings near:', targetLon, targetLat, 'radius:', radiusMeters, 'meters');
    
    const targetPosition = Cartesian3.fromDegrees(targetLon, targetLat);
    const highlightedFeatures: Cesium3DTileFeature[] = [];
    
    // Track previously highlighted features for efficient reset
    // Store on tileset to persist across function calls and tile loads
    if (!(tileset as any)._highlightedFeatures) {
      (tileset as any)._highlightedFeatures = new Set<Cesium3DTileFeature>();
    }
    const previouslyHighlighted = (tileset as any)._highlightedFeatures as Set<Cesium3DTileFeature>;
    
    // Helper function to get feature position
    // For OSM buildings, individual features don't have position metadata,
    // so we use the tile's bounding sphere center as an approximation
    const getFeaturePosition = (tile: any): Cartesian3 | null => {
      // Use tile's bounding sphere center (industry standard for OSM buildings)
      if (tile.boundingSphere && tile.boundingSphere.center) {
        return tile.boundingSphere.center;
      }
      // Fallback to content bounding sphere if available
      if (tile.content && tile.content.boundingSphere && tile.content.boundingSphere.center) {
        return tile.content.boundingSphere.center;
      }
      return null;
    };
    
    // Helper function to check and highlight a single feature
    const checkAndHighlightFeature = (feature: Cesium3DTileFeature, tile: any): boolean => {
      const featurePosition = getFeaturePosition(tile);
      
      if (!featurePosition) {
        return false;
      }
      
      // Calculate distance in meters (Cartesian3.distance returns meters in WGS84)
      const distance = Cartesian3.distance(targetPosition, featurePosition);
      
      if (distance <= radiusMeters) {
        // Highlight this building in yellow
        feature.color = Color.YELLOW.withAlpha(0.8);
        highlightedFeatures.push(feature);
        previouslyHighlighted.add(feature);
        console.log('✅ Highlighted building at distance:', distance.toFixed(2), 'meters');
        return true;
      } else {
        // Only reset if this feature was previously highlighted
        if (previouslyHighlighted.has(feature)) {
          feature.color = Color.WHITE;
          previouslyHighlighted.delete(feature);
        }
      }
      return false;
    };
    
    // Wait for tileset to be ready, then traverse tiles
    (tileset as any).readyPromise.then(() => {
      // Reset previously highlighted buildings (more efficient than resetting all)
      previouslyHighlighted.forEach(feature => {
        feature.color = Color.WHITE;
      });
      previouslyHighlighted.clear();
      
      // Traverse tiles and highlight buildings within radius
      const highlightNearbyBuildings = (tile: any) => {
        // Only process loaded tiles with content
        if (!tile.content || !tile.content.featuresLength) {
          return;
        }
        
        const content = tile.content;
        
        for (let i = 0; i < content.featuresLength; i++) {
          const feature = content.getFeature(i);
          checkAndHighlightFeature(feature, tile);
        }
        
        // Recursively process children (only if they exist and are loaded)
        if (tile.children) {
          for (const child of tile.children) {
            if (child && (child.content || child.children)) {
              highlightNearbyBuildings(child);
            }
          }
        }
      };
      
      // Traverse and highlight starting from root
      if (tileset.root) {
        highlightNearbyBuildings(tileset.root);
      }
      
      // Request render to show changes
      viewer.scene.requestRender();
      
      console.log('🏢 Highlighted', highlightedFeatures.length, 'buildings near searched location');
      
      // Set up tile load listener using closure to capture target position, radius, and helper functions
      // This ensures proper cleanup and reuses the same helper functions
      const createTileLoadListener = (targetPos: Cartesian3, radius: number) => {
        return (tile: any) => {
          if (!tile.content || !tile.content.featuresLength) {
            return;
          }
          
          const content = tile.content;
          const tilePosition = getFeaturePosition(tile);
          
          if (!tilePosition) {
            return;
          }
          
          // Quick check: if tile is far from target, skip processing (performance optimization)
          const tileDistance = Cartesian3.distance(targetPos, tilePosition);
          if (tileDistance > radius * 2) {
            // Tile is too far, skip individual feature checks
            return;
          }
          
          // Access the stored Set from tileset
          const storedHighlighted = (tileset as any)._highlightedFeatures as Set<Cesium3DTileFeature>;
          if (!storedHighlighted) {
            return;
          }
          
          // Process features in this tile
          for (let i = 0; i < content.featuresLength; i++) {
            const feature = content.getFeature(i);
            const featurePos = getFeaturePosition(tile);
            
            if (featurePos) {
              const distance = Cartesian3.distance(targetPos, featurePos);
              if (distance <= radius) {
                feature.color = Color.YELLOW.withAlpha(0.8);
                storedHighlighted.add(feature);
              } else if (storedHighlighted.has(feature)) {
                feature.color = Color.WHITE;
                storedHighlighted.delete(feature);
              }
            }
          }
          
          viewer.scene.requestRender();
        };
      };
      
      // Remove existing listener if present (cleanup)
      if ((tileset as any)._highlightTileLoadListener) {
        tileset.tileLoad.removeEventListener((tileset as any)._highlightTileLoadListener);
      }
      
      // Add new listener with closure
      const tileLoadListener = createTileLoadListener(targetPosition, radiusMeters);
      tileset.tileLoad.addEventListener(tileLoadListener);
      (tileset as any)._highlightTileLoadListener = tileLoadListener;
      
    }).catch((error: any) => {
      console.error('❌ Error highlighting buildings:', error);
    });
  };

  // Function to add patient leakage alert entities (circles and labels)
  const addPatientLeakageEntities = (viewer: Viewer, centerLon: number, centerLat: number) => {
    console.log('addPatientLeakageEntities called with:', centerLon, centerLat);
    
    // Always clear existing entities before adding new ones
    viewer.entities.removeAll();

    // Convert miles to degrees (approximate)
    // 1 mile ≈ 0.0145 degrees latitude
    // 1 mile ≈ 0.0145 * cos(latitude) degrees longitude
    const milesToLatDegrees = 0.0145;
    const milesToLonDegrees = 0.0145 * Math.cos(centerLat * Math.PI / 180);

    // Label 1: FL126 - Blue circle
    // Base coordinates (centered on Miami-Dade geocoded location)
    const fl126Lon = centerLon;
    const fl126Lat = centerLat;
    const fl126Radius = 3000; // meters (large radius for visibility when zoomed out)

    // Label 2: Metro Health - New Competitor ASC - Red circle
    // ~2.5 miles northeast from FL126
    const competitorLon = centerLon + (2.5 * milesToLonDegrees);
    const competitorLat = centerLat + (2.5 * milesToLatDegrees);
    const competitorRadius = 2500; // meters (large radius, will overlap with blue)

    // Label 3: Proposed Satellite Clinic - Lighter blue circle
    // ~0.8 miles from Label 2 (northeast)
    const satelliteLon = competitorLon + (0.8 * milesToLonDegrees);
    const satelliteLat = competitorLat + (0.8 * milesToLatDegrees);
    const satelliteRadius = 2000; // meters (large radius for visibility)

    // Add Label 1: FL126 (Blue circle)
    viewer.entities.add(new Entity({
      position: Cartesian3.fromDegrees(fl126Lon, fl126Lat),
      name: 'FL126',
      ellipse: {
        semiMajorAxis: fl126Radius,
        semiMinorAxis: fl126Radius,
        material: Color.BLUE.withAlpha(0.5),
        outline: true,
        outlineColor: Color.BLUE.withAlpha(1.0),
        outlineWidth: 4,
        heightReference: HeightReference.CLAMP_TO_GROUND,
        extrudedHeight: 0,
        show: true
      },
      label: {
        text: 'FL126',
        font: '18pt sans-serif',
        fillColor: Color.WHITE,
        outlineColor: Color.BLUE,
        outlineWidth: 3,
        style: 1, // FILL_AND_OUTLINE
        verticalOrigin: 1, // BOTTOM
        pixelOffset: new Cartesian2(0, -10),
        heightReference: HeightReference.CLAMP_TO_GROUND,
        scale: 1.0,
        show: true
      }
    }));

    // Add Label 2: Metro Health - New Competitor ASC (Red circle)
    viewer.entities.add(new Entity({
      position: Cartesian3.fromDegrees(competitorLon, competitorLat),
      name: 'Metro Health - New Competitor ASC (Est.)',
      ellipse: {
        semiMajorAxis: competitorRadius,
        semiMinorAxis: competitorRadius,
        material: Color.RED.withAlpha(0.5),
        outline: true,
        outlineColor: Color.RED.withAlpha(1.0),
        outlineWidth: 4,
        heightReference: HeightReference.CLAMP_TO_GROUND,
        extrudedHeight: 0,
        show: true
      },
      label: {
        text: 'Metro Health - New Competitor ASC (Est.)',
        font: '16pt sans-serif',
        fillColor: Color.WHITE,
        outlineColor: Color.RED,
        outlineWidth: 3,
        style: 1, // FILL_AND_OUTLINE
        verticalOrigin: 1, // BOTTOM
        pixelOffset: new Cartesian2(0, -10),
        heightReference: HeightReference.CLAMP_TO_GROUND,
        scale: 1.0,
        show: true
      }
    }));

    // Add Label 3: Proposed Satellite Clinic (Lighter blue circle)
    viewer.entities.add(new Entity({
      position: Cartesian3.fromDegrees(satelliteLon, satelliteLat),
      name: 'Proposed Satellite Clinic',
      ellipse: {
        semiMajorAxis: satelliteRadius,
        semiMinorAxis: satelliteRadius,
        material: Color.CYAN.withAlpha(0.5), // Lighter blue, increased opacity
        outline: true,
        outlineColor: Color.CYAN.withAlpha(1.0),
        outlineWidth: 4,
        heightReference: HeightReference.CLAMP_TO_GROUND,
        extrudedHeight: 0,
        show: true
      },
      label: {
        text: 'Proposed Satellite Clinic',
        font: '16pt sans-serif',
        fillColor: Color.WHITE,
        outlineColor: Color.CYAN,
        outlineWidth: 3,
        style: 1, // FILL_AND_OUTLINE
        verticalOrigin: 1, // BOTTOM
        pixelOffset: new Cartesian2(0, -10),
        heightReference: HeightReference.CLAMP_TO_GROUND,
        scale: 1.0,
        show: true
      }
    }));

    // Force scene to update and ensure entities are visible
    viewer.scene.requestRender();
    
    // Log to verify entities were added
    console.log('Entities added. Count:', viewer.entities.values.length);
    viewer.entities.values.forEach((entity, index) => {
      console.log(`Entity ${index}:`, entity.name, entity.position);
    });
  };

  // Note: All zoom, pan, and rotation controls are now handled by Cesium's built-in navigation
  // - Mouse wheel: zoom in/out
  // - Left-click drag: pan
  // - Right-click drag: rotate
  // - Middle-click drag: pan
  // - Touch gestures: pan, zoom, rotate
  // - Home button: resets camera to default view
  // - Navigation help button: shows keyboard shortcuts

  // Tray toggle handlers - close if already open, or open and close others
  const handleOpenCareNetworkTray = () => {
    if (showCareNetworkTray) {
      setShowCareNetworkTray(false);
    } else {
      setShowCareNetworkTray(true);
      setShowScenarioTray(false);
      setShowMarketIntelligenceTray(false);
      setShowPopulationCohortsTray(false);
    }
  };
  const handleOpenScenarioTray = () => {
    if (showScenarioTray) {
      setShowScenarioTray(false);
    } else {
      setShowScenarioTray(true);
      setShowCareNetworkTray(false);
      setShowMarketIntelligenceTray(false);
      setShowPopulationCohortsTray(false);
    }
  };
  const handleOpenMarketIntelligenceTray = () => {
    if (showMarketIntelligenceTray) {
      setShowMarketIntelligenceTray(false);
    } else {
      setShowMarketIntelligenceTray(true);
      setShowCareNetworkTray(false);
      setShowScenarioTray(false);
      setShowPopulationCohortsTray(false);
    }
  };
  const handleOpenPopulationCohortsTray = () => {
    if (showPopulationCohortsTray) {
      setShowPopulationCohortsTray(false);
    } else {
      setShowPopulationCohortsTray(true);
      setShowCareNetworkTray(false);
      setShowScenarioTray(false);
      setShowMarketIntelligenceTray(false);
    }
  };

  // Map toolbar handlers
  const handleToolbarModeChange = (mode: ToolbarMode) => {
    setToolbarMode(mode);
    console.log('Toolbar mode changed to:', mode);
    // TODO: Implement mode-specific logic for each tool
  };

  const handleGeoFilterSelect = (filterType: string) => {
    console.log('Geo filter selected:', filterType);
    // TODO: Implement visual filter logic in the future
  };

  const handleFullscreenToggle = () => {
    if (!document.fullscreenElement) {
      // Enter fullscreen
      document.documentElement.requestFullscreen?.();
      setIsFullscreen(true);
    } else {
      // Exit fullscreen
      document.exitFullscreen?.();
      setIsFullscreen(false);
    }
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  return (
    <Box className="three-d-map-root">
      {/* Removed header and description to save space */}
      <Box className={`three-d-map-container${
        showScenarioTray || showCareNetworkTray || showMarketIntelligenceTray || showPopulationCohortsTray ? ' tray-open' : ''
      }`}>
        <div
          ref={cesiumContainer}
          className={`cesium-container${
            showScenarioTray || showCareNetworkTray || showMarketIntelligenceTray || showPopulationCohortsTray ? ' map-tray-open' : ''
          }`}
        ></div>

        {/* Map Toolbar (bottom center) */}
        <MapToolbar
          activeMode={toolbarMode}
          isFullscreen={isFullscreen}
          onModeChange={handleToolbarModeChange}
          onGeoFilterSelect={handleGeoFilterSelect}
          onFullscreenToggle={handleFullscreenToggle}
          disabled={!isViewerReady}
          trayOpen={showScenarioTray || showCareNetworkTray || showMarketIntelligenceTray || showPopulationCohortsTray}
        />

        {/* Overlay Buttons */}
        <Box className={`overlay-buttons${
          showScenarioTray || showCareNetworkTray || showMarketIntelligenceTray || showPopulationCohortsTray ? ' tray-open' : ''
        }`}>
          <Button
            className="map-action-btn"
            variant="contained"
            sx={{
              backgroundColor: "#f5f5f5",
              color: "#222",
              boxShadow: 1,
              "&:hover": { backgroundColor: "#e0e0e0" },
            }}
            onClick={handleOpenCareNetworkTray}
          >
            Care Network
          </Button>
          <Button
            className="map-action-btn"
            variant="contained"
            sx={{
              backgroundColor: "#f5f5f5",
              color: "#222",
              boxShadow: 1,
              "&:hover": { backgroundColor: "#e0e0e0" },
            }}
            onClick={handleOpenScenarioTray}
          >
            Scenarios
          </Button>
          <Button
            className="map-action-btn"
            variant="contained"
            sx={{
              backgroundColor: "#f5f5f5",
              color: "#222",
              boxShadow: 1,
              "&:hover": { backgroundColor: "#e0e0e0" },
            }}
            onClick={handleOpenMarketIntelligenceTray}
          >
            Market Intel
          </Button>
          <Button
            className="map-action-btn"
            variant="contained"
            sx={{
              backgroundColor: "#f5f5f5",
              color: "#222",
              boxShadow: 1,
              "&:hover": { backgroundColor: "#e0e0e0" },
            }}
            onClick={handleOpenPopulationCohortsTray}
          >
            Cohorts
          </Button>
          
          {/* 2D View Toggle Button */}
          <Button
            className="map-action-btn"
            variant="outlined"
            startIcon={<MapIcon />}
            onClick={() => navigate('/map')}
            sx={{
              borderColor: 'rgba(138,43,226,0.3)',
              color: '#FFFFFF',
              backgroundColor: 'rgba(11,11,11,0.8)',
              boxShadow: 1,
              marginLeft: 1,
              '&:hover': {
                borderColor: 'rgba(0,180,255,0.5)',
                backgroundColor: 'rgba(0,180,255,0.1)'
              }
            }}
          >
            2D View
          </Button>
          
          {/* View Management Buttons */}
          <Button
            className="map-action-btn"
            variant="contained"
            startIcon={<SaveIcon />}
            sx={{
              backgroundColor: "#2196f3",
              color: "#fff",
              boxShadow: 1,
              "&:hover": { backgroundColor: "#1976d2" },
              marginLeft: 1,
            }}
            onClick={() => console.log('Save View clicked')}
          >
            Save View
          </Button>
          <Button
            className="map-action-btn"
            variant="contained"
            startIcon={<FolderOpenIcon />}
            sx={{
              backgroundColor: "#4caf50",
              color: "#fff",
              boxShadow: 1,
              "&:hover": { backgroundColor: "#388e3c" },
            }}
            onClick={() => console.log('Load View clicked')}
          >
            Load View
          </Button>
        </Box>
        {/* Scenario Builder Tray (custom, not Drawer) */}
        {showScenarioTray && (
          <ScenarioBuilderTray onClose={() => setShowScenarioTray(false)} />
        )}
        {/* Care Network Tray */}
        {showCareNetworkTray && (
          <Box className="care-network-tray-wrapper">
            <CareNetworkTray onClose={() => setShowCareNetworkTray(false)} />
          </Box>
        )}
        {/* Market Intelligence Tray */}
        {showMarketIntelligenceTray && (
          <MarketIntelligenceTray onClose={() => setShowMarketIntelligenceTray(false)} />
        )}
        {/* Population Cohorts Tray */}
        {showPopulationCohortsTray && (
          <PopulationCohortsTray onClose={() => setShowPopulationCohortsTray(false)} />
        )}
      </Box>
    </Box>
  );
};

export default ThreeDMap;